import * as PIXI from "pixi.js";
import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * perfProbe — one console line per second, so a frame problem can be READ.
 *
 * Off behind FEATURES_CONFIG.DEV_TOOLS.PERF_PROBE, like every other trace in
 * this game. Nothing here runs, and nothing is patched, while that flag is
 * false.
 *
 * ── What it is for ──
 *
 * "It feels slower" is not something that can be fixed. This turns it into
 * numbers that rank the suspects against each other:
 *
 *   ms p95        the frame time that actually hurts, not the average
 *   draws         GPU draw calls per frame — masks and blend changes show here
 *   ticking       spines whose skeletons are SOLVED every frame
 *   HIDDEN        of those, the ones nobody can see. This should be ZERO.
 *                 Anything else is CPU burnt on a picture that is not drawn —
 *                 see [[alpha-0001-is-not-hidden]].
 *   bones/slots   the size of that solve, which is what it really costs
 *   objs          drawn / total in the scene graph
 *   ticker        Ticker.shared listeners — a number that only ever grows is a
 *                 leak
 *
 * The HIDDEN rigs are named by shape (`13b/17s` is the mystery lid, `244b/248s`
 * is win_celebration), because a pooled spine has no name to report.
 */

const SAMPLE_MS = 1000;

const state = {
  on: false,
  calls: 0,
  frames: 0,
  frameTimes: [],
  origElements: null,
  origArrays: null,
  tickerFn: null,
  interval: null,
  last: 0,
  gl: null,
};

const isSpine = (o) => !!(o && o.skeleton && o.state && o.skeleton.bones);

// PIXI skips a subtree on any one of these, so all three decide "is it drawn".
const isDrawn = (o) => o.visible && o.renderable && o.worldAlpha > 0;

/**
 * Walk the whole tree once.
 *
 * EVERY node, hidden ones included. Skipping hidden subtrees would match what
 * the GPU does and would miss the entire point — a spine that is hidden and
 * still solving is the waste being hunted, and it lives under a hidden parent
 * by definition.
 */
const walk = (stage) => {
  const out = {
    nodes: 0,
    nodesDrawn: 0,
    spines: 0,
    spinesDrawn: 0,
    ticking: 0,
    hidden: 0,
    bones: 0,
    slots: 0,
    masks: 0,
    rigs: new Map(),
  };

  const visit = (node, parentDrawn) => {
    out.nodes += 1;
    const vis = parentDrawn && isDrawn(node);
    if (vis) out.nodesDrawn += 1;
    if (node.mask && vis) out.masks += 1;

    if (isSpine(node)) {
      out.spines += 1;
      if (vis) out.spinesDrawn += 1;
      if (node.autoUpdate) {
        out.ticking += 1;
        out.bones += node.skeleton.bones.length;
        out.slots += node.skeleton.slots.length;
        if (!vis) {
          out.hidden += 1;
          const rig = `${node.skeleton.bones.length}b/${node.skeleton.slots.length}s`;
          out.rigs.set(rig, (out.rigs.get(rig) || 0) + 1);
        }
      }
    }

    const kids = node.children;
    if (!kids) return;
    for (let i = 0; i < kids.length; i += 1) visit(kids[i], vis);
  };

  visit(stage, true);
  return out;
};

const p95 = (arr) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))];
};

const sample = () => {
  const stage = globalThis.__PIXI_STAGE__;
  const renderer = globalThis.__PIXI_RENDERER__;
  if (!stage || !renderer) return;

  const w = walk(stage);
  const ft = state.frameTimes;
  const frames = state.frames || 1;
  const avg = ft.reduce((a, b) => a + b, 0) / (ft.length || 1);

  const hidden =
    w.hidden > 0
      ? `${w.hidden} HIDDEN [${[...w.rigs.entries()]
          .map(([rig, n]) => `${rig} x${n}`)
          .join(" ")}]`
      : "0 hidden";

  console.log(
    `[PERF] fps ${String(state.frames).padStart(3)} | ` +
      `ms avg ${avg.toFixed(1).padStart(5)} p95 ${p95(ft).toFixed(1).padStart(5)} max ${Math.max(0, ...ft).toFixed(1).padStart(6)} | ` +
      `draws ${String(Math.round(state.calls / frames)).padStart(4)} | ` +
      `spines ${w.spinesDrawn}/${w.spines} drawn, ${w.ticking} ticking (${hidden}) | ` +
      `bones ${w.bones} slots ${w.slots} | ` +
      `objs ${w.nodesDrawn}/${w.nodes} | masks ${w.masks} | ` +
      `ticker ${PIXI.Ticker.shared.count} | tex ${renderer.texture.managedTextures.length}`,
  );

  state.calls = 0;
  state.frames = 0;
  state.frameTimes = [];
};

/**
 * Start sampling once the renderer exists.
 *
 * Stage publishes the globals inside an effect, so this retries on the next
 * frame rather than assuming — the same shape SpineAtlasUploader uses.
 */
export const startPerfProbe = (retry = 0) => {
  if (!FEATURES_CONFIG.DEV_TOOLS?.PERF_PROBE || state.on) return;

  const renderer = globalThis.__PIXI_RENDERER__;
  if (!renderer?.gl) {
    if (retry < 120) requestAnimationFrame(() => startPerfProbe(retry + 1));
    return;
  }

  state.on = true;
  state.gl = renderer.gl;

  // Draw-call counter. Both entry points: the batcher uses drawElements, some
  // paths use drawArrays.
  state.origElements = state.gl.drawElements.bind(state.gl);
  state.origArrays = state.gl.drawArrays.bind(state.gl);
  state.gl.drawElements = (...a) => {
    state.calls += 1;
    return state.origElements(...a);
  };
  state.gl.drawArrays = (...a) => {
    state.calls += 1;
    return state.origArrays(...a);
  };

  state.last = performance.now();
  state.tickerFn = () => {
    const now = performance.now();
    state.frameTimes.push(now - state.last);
    state.last = now;
    state.frames += 1;
  };
  // LAST in the ticker order, so what it measures is the whole frame.
  PIXI.Ticker.shared.add(state.tickerFn, null, -100);

  state.interval = setInterval(sample, SAMPLE_MS);
  console.log(
    "[PERF] probe on — one line per second. HIDDEN should be 0; anything else is CPU spent on something nobody can see.",
  );
};

/** Put the WebGL context back exactly as it was found. */
export const stopPerfProbe = () => {
  if (!state.on) return;
  state.on = false;
  clearInterval(state.interval);
  PIXI.Ticker.shared.remove(state.tickerFn);
  if (state.gl) {
    state.gl.drawElements = state.origElements;
    state.gl.drawArrays = state.origArrays;
  }
  console.log("[PERF] probe off");
};
